/**
 * Service detail pages.
 *
 * There is no approved design or copy for these pages — design/README.md lists
 * them as still to design. Everything here is either lifted from the homepage's
 * approved engagement-model panel or a labelled placeholder. Nothing is invented.
 *
 * The AI-Driven Validation page additionally carries sections transcribed from
 * WeAreQA-AI-Driven.pdf; that deck covers only that service.
 */

import { models, type EngagementModel } from "./models";

export type Service = {
  slug: string;
  href: string;
  model: EngagementModel;
};

/** Slugs follow the service name. See the note in CLAUDE.md before changing. */
export const aiDrivenValidation: Service = {
  slug: "ai-driven-validation",
  href: "/services/ai-driven-validation",
  model: models[0],
};

export const qaConsulting: Service = {
  slug: "qa-consulting-modernization",
  href: "/services/qa-consulting-modernization",
  model: models[1],
};

export const qualityOperations: Service = {
  slug: "intelligent-quality-operations",
  href: "/services/intelligent-quality-operations",
  model: models[2],
};

/**
 * The fourth service. It has no homepage engagement panel — the approved design
 * sets three models — so it carries no badge, stat or capability cards and does
 * not use the shared service template. Its page is built entirely from the
 * client's page-test-architecture.md.
 */
export const testArchitecture = {
  slug: "qa-architecture-engineering",
  href: "/services/qa-architecture-engineering",
  title: "Test Architecture & QA Platform Engineering",
} as const;

/**
 * Both CTAs point at homepage sections rather than at standalone pages: the
 * call booking lives at /#book and the maturity check at /#assessment. Client
 * request -- the contact form and the hire-talent page are a longer route to
 * the same two actions. Every service, industry and case-study page reads this
 * object, and About reads the hrefs while keeping its own secondary label.
 */
export const servicePageChrome = {
  breadcrumbRoot: { label: "Services", href: "#" },
  includedEyebrow: "What's included",
  ctaPrimary: { label: "Schedule an Engineering Call", href: "/#book" },
  ctaSecondary: { label: "Take the QA maturity check", href: "/#assessment" },
} as const;

/**
 * THE SERVICE-PAGE PLACEHOLDER SCAFFOLD IS GONE, along with `ServiceOutline`,
 * the component that rendered it. Removed at the client's request.
 *
 * /services/ai-driven-validation now ends at Key Benefits and goes straight to
 * the CTA. What those slots were holding open, so it is not lost:
 *
 * - **How it works** — the engagement stages, start to handover. Deck slides
 *   10-12 cover this (three phases, 17 steps). Built once and reverted as too
 *   long to read; if rebuilt it needs condensing, not transcribing.
 * - **What you get** — deliverables and cadence. Slides 10-12 name a deliverable
 *   per step; **cadence appears nowhere in the deck** and must come from the
 *   client.
 * - **Case study** — no client-shape study exists for this service. The three
 *   AI studies at /case-studies are the nearest real proof.
 * - **Starting-from price** — still missing, and homepage FAQ 6 tells visitors
 *   every service page carries one.
 * - **Service FAQ** — the homepage FAQ covers the common questions.
 *
 * The page is now shorter and finished-looking rather than long and visibly
 * unfinished. That is a presentation decision, not a content one: the gaps above
 * are still gaps.
 */
